import { useState } from "react";
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import tabletImage from '@/assets/tablet-login.webp';
import bigLogo from '@/assets/big-logo.svg';
import { useNavigate, useParams } from "react-router-dom";
import { resetPasswordApi } from '@/services/resetPasswordSevice';
import toast from "react-hot-toast";

interface ResetPasswordProps {
  onLogin: (
    email: string,
    password: string,
  ) => Promise<{ firstName: string; lastName: string } | void>;
  isLoading: boolean;
  error: string | null;
}

const ResetPassword = ({ onLogin: _onLogin, isLoading, error }: ResetPasswordProps) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [changePasswordEnd, setChangePasswordEnd] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (localError) setLocalError(null);
    if (successMessage) setSuccessMessage(null);
  };

  const handleSubmit = async () => {
    setLocalError(null);
    setSuccessMessage(null);

    try {
      const response: any = await resetPasswordApi.resetPassword(
        token || "",
        formData.password,
      );
      if (response?.success) {
        setFormData({
          password: "",
          confirmPassword: "",
        });
        setChangePasswordEnd(true);
        setSuccessMessage(
          "Contraseña restablecida con éxito. Ahora puedes iniciar sesión.",
        );
        toast.success(
          "Contraseña restablecida con éxito. Ahora puedes iniciar sesión.",
          { duration: 8000 },
        );
        navigate("/login");
      } else {
        setChangePasswordEnd(false);
        setLocalError(
          response?.message ||
            "Error al restablecer la contraseña, intenta de nuevo mas tarde.",
        );
      }
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setLocalError(err?.message ?? "Error desconocido");
      console.error("🚨 Error de login:", err);
    }
  };

  const displayError = error || localError;

  return (
    <div className="h-screen bg-white flex overflow-hidden">
      {/* Left Side - Form Section */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-8">
        <div className="w-full max-w-md">
          {/* Logo - Centrado y Grande */}
          <div className="mb-8 flex flex-col items-center">
            <img src={bigLogo} alt="SLYRSA Logo" className="h-20 w-auto mb-2" />
          </div>

          {/* Welcome Text */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Recuperar contraseña
            </h1>
            <p className="text-sm text-gray-600">
              Ingresa tu nueva contraseña para restablecer tu cuenta.
            </p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-green-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">
                    {successMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {displayError && !successMessage && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                <p className="ml-3 text-sm text-red-800">{displayError}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <div className="space-y-4">
            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border-x-0 border-t-0 border-b-1 border-[#787A7D] rounded-none focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                  placeholder="••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/*Confirm Password Field */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                Confirmar Contraseña
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border-x-0 border-t-0 border-b-1 border-[#787A7D] rounded-none focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                  placeholder="••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="button"
              disabled={isLoading || changePasswordEnd}
              onClick={() => handleSubmit()}
              className={estiloDeBoton('primario', 'w-full')}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Cambiando contraseña...
                </div>
              ) : (
                "Recuperar contraseña"
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Right Side - Features Section with Gradient in Rounded Box */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-6 bg-gray-50">
        {/* Rounded White Container */}
        <div className="bg-gradient-to-br from-[#8B7EC8] via-[#6B8DD6] to-[#5B9FE3] rounded-[2.5rem] p-10 w-full h-[calc(100vh-3rem)] flex flex-col items-center justify-between relative overflow-hidden shadow-xl">
          {/* Features List - Más abajo */}
          <div className="relative z-10 w-full max-w-lg space-y-4 mt-12">
            {/* Feature 1 */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-[2rem] p-4 flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-5 h-5 bg-green-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-white text-sm leading-relaxed">
                Facilita, monitorea y da seguimiento a la selección de personal.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-[2rem] p-4 flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-5 h-5 bg-green-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-white text-sm leading-relaxed">
                Centraliza todo el proceso de reclutamiento, desde la solicitud
                de vacantes hasta la contratación final.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-[2rem] p-4 flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-5 h-5 bg-green-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-white text-sm leading-relaxed">
                Optimiza la comunicación entre áreas y agiliza la toma de
                decisiones en cada etapa del proceso de selección.
              </p>
            </div>
          </div>

          {/* Tablet Image - Alineado al fondo */}
          <div className="relative z-10 w-full max-w-2xl flex items-end justify-center flex-shrink-0 mt-auto">
            <img
              src={tabletImage}
              alt="Dashboard en tablet"
              className="relative sm:top-[40px] top-[18px] w-full h-auto drop-shadow-2xl object-contain"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
